import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const FxBasisControls = withListOverflow(FxBasisListOwner);
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getFxBudgetRates,
  getFxConversionBasis,
  previewFxConversionBasis,
  saveFxManualHistoryRate,
  saveFxBudgetRate,
  saveFxConversionBasis,
} from "../../data/fxRatesClient.js";
import { T } from "../../app/tokens.js";
import { formatFxBasisLabel } from "../../data/useFxConversionBasis.js";

const CURRENCY_SYMBOLS = {
  USD: "$", GBP: "£", EUR: "€", AUD: "A$", CAD: "C$", SGD: "S$",
  AED: "د.إ", HKD: "HK$", CHF: "Fr", JPY: "¥", CNY: "¥",
  BRL: "R$", INR: "₹", ISK: "kr",
};
const FIXED_RATES = new Set(["USD", "AED", "HKD"]);
const PANEL = { border: `1px solid ${T.border}`, borderRadius: 8, padding: 14, margin: "14px 0", background: T.white };
const BUTTON = { border: `1px solid ${T.border}`, borderRadius: 6, background: T.white, color: T.dark, padding: "6px 11px", fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: "pointer" };
const PRIMARY = { ...BUTTON, borderColor: T.blue, background: T.blue, color: T.white };
const TH = { padding: "8px 10px", textAlign: "right", borderBottom: `1px solid ${T.border}`, color: T.mid, fontSize: T.fsXxs, textTransform: "uppercase", letterSpacing: ".05em", whiteSpace: "nowrap" };
const TD = { padding: "8px 10px", textAlign: "right", borderBottom: `1px solid ${T.lightGray}`, fontSize: T.fsSm, fontVariantNumeric: "tabular-nums", verticalAlign: "middle" };

function dateLabel(value) {
  if (!value) return "—";
  const iso = String(value).slice(0, 10);
  const parsed = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? iso
    : parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

function isBootstrapQuote(source) {
  return typeof source === "string" && source.startsWith("bootstrap-existing-quote:");
}

function sameBasis(a, b) {
  return Boolean(a && b && a.mode === b.mode && a.asOfDate === b.asOfDate && Number(a.fiscalYear || 0) === Number(b.fiscalYear || 0));
}

function FxBasisListOwner({ currentRole, currencyData }) {
  const isAdmin = currentRole === "admin";
  const [savedResponse, setSavedResponse] = useState(null);
  const [basisDraft, setBasisDraft] = useState(null);
  const [preview, setPreview] = useState(null);
  const [budgetRows, setBudgetRows] = useState([]);
  const [budgetDraft, setBudgetDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [manualRateDraft, setManualRateDraft] = useState(null);
  const [manualRateError, setManualRateError] = useState("");
  const [manualRateBusy, setManualRateBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const confirmDialogRef = useRef(null);
  const confirmCancelRef = useRef(null);
  const returnFocusRef = useRef(null);
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const budgetMode = basisDraft?.mode === "budget";
  const budgetFiscalYear = Number(basisDraft?.fiscalYear);

  useEffect(() => {
    let active = true;
    getFxConversionBasis()
      .then((basisResult) => {
        if (!active) return;
        setSavedResponse(basisResult);
        setBasisDraft({ ...basisResult.basis });
        setPreview(basisResult);
      })
      .catch((cause) => {
        if (active) setError(cause?.message || "Could not load the saved FX conversion basis.");
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const year = budgetFiscalYear;
    if (!budgetMode || !Number.isInteger(year) || year < 2000 || year > 2100) return undefined;
    let active = true;
    setBudgetRows([]);
    setBudgetDraft({});
    getFxBudgetRates(year)
      .then((result) => {
        if (!active) return;
        const rows = Array.isArray(result?.rates) ? result.rates : [];
        setBudgetRows(rows);
        setBudgetDraft(Object.fromEntries(rows.map((row) => [row.currency, String(row.rateToUsd ?? "")])));
      })
      .catch((cause) => {
        if (active) setError(cause?.message || "Could not load budget rates.");
      });
    return () => { active = false; };
  }, [budgetMode, budgetFiscalYear]);

  useEffect(() => {
    const normalizedBasis = basisDraft?.mode === "budget"
      ? { ...basisDraft, fiscalYear: Number(basisDraft.fiscalYear) }
      : basisDraft;
    if (!normalizedBasis
      || (normalizedBasis.mode === "as_of" && !normalizedBasis.asOfDate)
      || (normalizedBasis.mode === "budget"
        && (!Number.isInteger(normalizedBasis.fiscalYear) || normalizedBasis.fiscalYear < 2000 || normalizedBasis.fiscalYear > 2100))) {
      setPreview(null);
      return undefined;
    }
    let active = true;
    setPreviewLoading(true);
    previewFxConversionBasis(normalizedBasis)
      .then((result) => { if (active) { setPreview(result); setError(""); } })
      .catch((cause) => { if (active) setError(cause?.message || "Could not preview this FX basis."); })
      .finally(() => { if (active) setPreviewLoading(false); });
    return () => { active = false; };
  }, [basisDraft]);

  useEffect(() => {
    if (!confirmAction) return undefined;
    const returnFocusTo = returnFocusRef.current;
    confirmCancelRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape" && !busyRef.current) {
        event.preventDefault();
        setConfirmAction(null);
        return;
      }
      if (event.key !== "Tab") return;
      const buttons = [...(confirmDialogRef.current?.querySelectorAll("button:not(:disabled)") || [])];
      if (!buttons.length) return;
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (returnFocusTo?.isConnected) returnFocusTo.focus();
    };
  }, [confirmAction]);

  const savedBasis = savedResponse?.basis ?? null;
  const isBasisDirty = !sameBasis(basisDraft, savedBasis);
  const currencies = useMemo(() => {
    const codes = new Set([
      ...(currencyData?.rates || []).map((row) => row.currency),
      ...Object.keys(currencyData?.liveSnapshot?.rates || {}),
      ...(preview?.rates || []).map((row) => row.currency),
    ]);
    return [...codes].sort((a, b) => (a === "USD" ? -1 : b === "USD" ? 1 : a.localeCompare(b)));
  }, [currencyData, preview]);
  const previewRates = preview?.rates || [];
  const rateByCode = new Map(previewRates.map((row) => [row.currency, row]));
  const budgetByCode = new Map(budgetRows.map((row) => [row.currency, row]));
  const hasBudgetEdits = basisDraft?.mode === "budget" && currencies.some((currency) => (
    currency !== "USD"
    && !FIXED_RATES.has(currency)
    && budgetDraft[currency] !== undefined
    && budgetDraft[currency] !== String(budgetByCode.get(currency)?.rateToUsd ?? "")
  ));
  const hasUncommittedChanges = isBasisDirty || hasBudgetEdits;
  const basisModeLabel = basisDraft ? formatFxBasisLabel(basisDraft) : "";
  const savedModeLabel = savedBasis ? formatFxBasisLabel(savedBasis) : "";

  function setMode(mode) {
    setError("");
    setSuccess("");
    setBasisDraft((old) => old ? ({
      ...old,
      mode,
      asOfDate: mode === "as_of" ? (old.asOfDate || new Date().toISOString().slice(0, 10)) : null,
      fiscalYear: mode === "budget" ? (old.fiscalYear || new Date().getUTCFullYear()) : null,
    }) : old);
  }

  function cancelDraft() {
    setBasisDraft({ ...savedBasis });
    setBudgetDraft(savedBasis?.mode === "budget" && Number(savedBasis.fiscalYear) === Number(basisDraft?.fiscalYear)
      ? Object.fromEntries(budgetRows.map((row) => [row.currency, String(row.rateToUsd ?? "")]))
      : {});
    setSuccess("");
    setError("");
  }

  function openConfirmation(action, trigger) {
    returnFocusRef.current = trigger ?? document.activeElement;
    setConfirmAction(action);
  }

  async function loadSavedBasis() {
    const next = await getFxConversionBasis();
    setSavedResponse(next);
    setBasisDraft({ ...next.basis });
    setPreview(next);
  }

  async function retryBasisLoad() {
    setLoading(true);
    setError("");
    try {
      await loadSavedBasis();
    } catch (cause) {
      setError(cause?.message || "Could not load the saved FX conversion basis.");
    } finally {
      setLoading(false);
    }
  }

  async function persistBasis() {
    if (busy || !basisDraft) return;
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const basisToSave = basisDraft.mode === "budget"
        ? { ...basisDraft, fiscalYear: Number(basisDraft.fiscalYear) }
        : basisDraft;
      await saveFxConversionBasis(basisToSave);
      await loadSavedBasis();
      setSuccess("Conversion basis saved. USD figures are refreshing across the app and reports.");
      setConfirmAction(null);
    } catch (cause) {
      setError(cause?.message || "Could not save the conversion basis.");
    } finally {
      setBusy(false);
    }
  }

  async function persistBudgetRate(currency) {
    if (busy || !basisDraft) return;
    const raw = budgetDraft[currency] ?? "";
    const value = Number(raw);
    if (!raw.trim() || !Number.isFinite(value) || value <= 0) {
      setError(`Enter a positive rate for ${currency}.`);
      return;
    }
    setBusy(true);
    setError("");
    setSuccess("");
    try {
      const old = budgetByCode.get(currency);
      await saveFxBudgetRate({
        fiscalYear: Number(basisDraft.fiscalYear),
        currency,
        rateToUsd: value,
        rateDate: old?.rateDate || `${basisDraft.fiscalYear}-01-01`,
        source: old?.source || "manual",
      });
      const result = await getFxBudgetRates(Number(basisDraft.fiscalYear));
      const rows = Array.isArray(result?.rates) ? result.rates : [];
      setBudgetRows(rows);
      setBudgetDraft(Object.fromEntries(rows.map((row) => [row.currency, String(row.rateToUsd ?? "")])));
      setSuccess(`${currency} FY${String(basisDraft.fiscalYear).slice(-2)} budget rate saved.`);
      setConfirmAction(null);
      if (savedBasis?.mode === "budget" && Number(savedBasis.fiscalYear) === Number(basisDraft.fiscalYear)) {
        await loadSavedBasis();
      }
    } catch (cause) {
      setError(cause?.message || `Could not save the ${currency} budget rate.`);
    } finally {
      setBusy(false);
    }
  }

  async function persistManualRate() {
    if (!isAdmin || manualRateBusy || !manualRateDraft) return;
    const rate = Number(manualRateDraft.rateToUsd);
    if (!manualRateDraft.rateToUsd.trim() || !Number.isFinite(rate) || rate <= 0) {
      setManualRateError("Enter a positive rate.");
      return;
    }
    if (!manualRateDraft.rateDate) {
      setManualRateError("Choose the date for this rate.");
      return;
    }
    setManualRateBusy(true);
    setManualRateError("");
    setError("");
    setSuccess("");
    try {
      await saveFxManualHistoryRate({
        currency: manualRateDraft.currency,
        rateToUsd: rate,
        rateDate: manualRateDraft.rateDate,
      });
      setSuccess(`${manualRateDraft.currency} manual rate saved for ${manualRateDraft.rateDate}.`);
      setManualRateDraft(null);
      const normalizedBasis = basisDraft.mode === "budget"
        ? { ...basisDraft, fiscalYear: Number(basisDraft.fiscalYear) }
        : basisDraft;
      try {
        const nextPreview = await previewFxConversionBasis(normalizedBasis);
        setPreview(nextPreview);
      } catch (cause) {
        setError(`Manual rate saved, but the rate view could not refresh: ${cause?.message || "preview unavailable"}`);
      }
    } catch (cause) {
      setManualRateError(cause?.message || "Could not save the manual rate.");
    } finally {
      setManualRateBusy(false);
    }
  }

  async function confirmSave() {
    if (confirmAction?.type === "basis") return persistBasis();
    if (confirmAction?.type === "budget") return persistBudgetRate(confirmAction.currency);
  }

  if (loading) {
    return <div data-testid="fx-basis-loading" role="status" style={{ ...PANEL, color: T.mid }}>Loading saved conversion basis…</div>;
  }

  if (!savedResponse || !basisDraft) {
    return (
      <section data-testid="fx-basis-load-error" role="alert" style={{ ...PANEL, borderColor: T.red }}>
        <strong style={{ color: T.red }}>Conversion basis unavailable.</strong>
        <p style={{ margin: "7px 0 10px", color: T.mid, fontSize: T.fsSm }}>
          {error || "The saved FX conversion basis could not be loaded. No basis changes can be made until it is available."}
        </p>
        <button type="button" disabled={busy} onClick={retryBasisLoad} style={BUTTON} data-testid="fx-basis-retry">
          Retry
        </button>
      </section>
    );
  }

  return (
    <section data-testid="fx-basis-controls" style={PANEL} aria-labelledby="fx-basis-heading">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", flexWrap: "wrap" }}>
        <h3 id="fx-basis-heading" style={{ margin: 0, fontSize: T.fsMd, color: T.dark }}>Conversion basis</h3>
        <span style={{ color: T.mid, fontSize: T.fsXs }} data-testid="fx-basis-saved-label">
          Saved: {savedModeLabel || "unavailable"}
        </span>
      </div>
      <p style={{ margin: "5px 0 12px", color: T.mid, fontSize: T.fsSm }}>
        One saved basis drives USD conversion throughout the app and reports. Changes below are previews until saved.
      </p>

      <div role="group" aria-label="Conversion basis" style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 11 }}>
        {[
          ["live", "Live"],
          ["as_of", "As-of date"],
          ["budget", "FY budget"],
        ].map(([mode, label]) => (
          <button
            key={mode}
            type="button"
            aria-pressed={basisDraft?.mode === mode}
            disabled={!isAdmin || busy}
            onClick={() => setMode(mode)}
            data-testid={`fx-basis-mode-${mode}`}
            style={{ ...BUTTON, borderColor: basisDraft?.mode === mode ? T.blue : T.border, background: basisDraft?.mode === mode ? T.blueSoft : T.white, color: basisDraft?.mode === mode ? T.blue : T.dark, opacity: isAdmin ? 1 : 0.72 }}
          >
            {label}
          </button>
        ))}
        {basisDraft?.mode === "as_of" && (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.dark, fontSize: T.fsSm }}>
            As of
            <input
              type="date"
              value={basisDraft.asOfDate || ""}
              disabled={!isAdmin || busy}
              onChange={(event) => setBasisDraft((old) => ({ ...old, asOfDate: event.target.value }))}
              data-testid="fx-basis-date"
              aria-label="As-of date"
            />
          </label>
        )}
        {basisDraft?.mode === "budget" && (
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, color: T.dark, fontSize: T.fsSm }}>
            Fiscal year
            <input
              type="number"
              min="2000"
              max="2100"
              step="1"
              value={basisDraft.fiscalYear ?? ""}
              disabled={!isAdmin || busy}
              onChange={(event) => setBasisDraft((old) => ({ ...old, fiscalYear: event.target.value }))}
              data-testid="fx-basis-fiscal-year"
              aria-label="Budget fiscal year"
              style={{ width: 92 }}
            />
          </label>
        )}
      </div>

      {hasUncommittedChanges && (
        <div data-testid="fx-basis-preview-bar" role="status" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", background: T.ragAmberBg, border: `1px solid ${T.ragAmberBorder}`, borderRadius: 7, padding: "9px 11px", marginBottom: 12, color: T.dark, fontSize: T.fsSm }}>
          <strong>
            {isBasisDirty
              ? `Preview: ${basisModeLabel} — not yet applied.`
              : "Budget-rate edits are not yet saved."}
          </strong>
          <span style={{ flex: 1 }} />
          <button type="button" disabled={!isAdmin || busy} onClick={cancelDraft} style={BUTTON} data-testid="fx-basis-cancel">Cancel</button>
          {isBasisDirty && (
            <button type="button" disabled={!isAdmin || busy || previewLoading || !preview} onClick={(event) => openConfirmation({ type: "basis" }, event.currentTarget)} style={PRIMARY} data-testid="fx-basis-save">
              Save basis
            </button>
          )}
        </div>
      )}

      <div role="note" data-testid="fx-basis-saved-banner" style={{ display: "flex", gap: 9, background: T.greenSoft, border: `1px solid ${T.green}`, borderRadius: 7, padding: "9px 11px", marginBottom: 12, color: T.dark, fontSize: T.fsSm }}>
        <span aria-hidden="true" style={{ color: T.green }}>●</span>
        <span><strong>Saved basis: {savedModeLabel}.</strong> Every USD figure uses this basis. {isAdmin ? "Only saving a preview applies it." : "You can view the selected basis; only admins can change it."}</span>
      </div>

      {error && <div role="alert" data-testid="fx-basis-error" style={{ color: T.red, margin: "8px 0", fontSize: T.fsSm }}>{error}</div>}
      {success && <div role="status" data-testid="fx-basis-success" style={{ color: T.green, margin: "8px 0", fontSize: T.fsSm }}>{success}</div>}
      {previewLoading && <div role="status" data-testid="fx-basis-preview-loading" style={{ color: T.mid, marginBottom: 6, fontSize: T.fsXs }}>Loading preview rates…</div>}

      <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 790 }}>
          <thead>
            <tr>
              <th style={{ ...TH, textAlign: "left" }}>Currency</th>
              <th style={TH}>Rate used ({basisDraft?.mode === "live" ? "live" : basisDraft?.mode === "budget" ? "budget" : "as-of"})</th>
              <th style={TH}>Basis date</th>
              <th style={TH}>Live rate</th>
              <th style={TH}>Drift vs live</th>
              <th style={{ ...TH, textAlign: "left" }}>Source</th>
              <th style={TH}>Manual rate</th>
            </tr>
          </thead>
          <tbody>
            {currencies.map((currency) => {
              const detail = rateByCode.get(currency);
              const budget = budgetByCode.get(currency);
              const rawPreviewRate = basisDraft?.mode === "budget"
                ? (budgetDraft[currency] ?? (budget?.rateToUsd == null ? "" : String(budget.rateToUsd)))
                : detail?.rateToUsd;
              const used = rawPreviewRate === "" ? null : Number(rawPreviewRate);
              const live = currencyData?.liveSnapshot?.rates?.[currency];
              const liveSource = currencyData?.liveSnapshot?.sources?.[currency];
              const hasLiveQuote = Number.isFinite(Number(live))
                && Number(live) > 0
                && !isBootstrapQuote(liveSource);
              const drift = hasLiveQuote && Number.isFinite(used) && used > 0
                ? ((Number(live) - used) / used) * 100
                : null;
              const isEdited = basisDraft?.mode === "budget"
                && budgetDraft[currency] !== undefined
                && budgetDraft[currency] !== String(budget?.rateToUsd ?? "");
              const source = basisDraft?.mode === "budget" ? (isEdited ? "Manual (unsaved)" : budget?.source || "No budget rate") : detail?.source;
              const rateDate = basisDraft?.mode === "budget" ? (budget?.rateDate || `${basisDraft.fiscalYear}-01-01`) : detail?.rateDate;
              return (
                <tr key={currency} data-testid={`fx-basis-rate-${currency}`}>
                  <td style={{ ...TD, textAlign: "left", whiteSpace: "nowrap" }}>
                    <strong>{currency}</strong><span style={{ marginLeft: 7, color: T.light }}>{CURRENCY_SYMBOLS[currency] || ""}</span>
                  </td>
                  <td style={{ ...TD, fontWeight: T.fwBold }}>
                    {basisDraft?.mode === "budget" && currency !== "USD" ? (
                      <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                        <input
                          type="number"
                          min="0.000001"
                          step="0.000001"
                          value={budgetDraft[currency] ?? ""}
                          placeholder="Enter rate"
                          disabled={!isAdmin || busy || FIXED_RATES.has(currency)}
                          onChange={(event) => setBudgetDraft((old) => ({ ...old, [currency]: event.target.value }))}
                          aria-label={`${currency} budget rate`}
                          data-testid={`fx-budget-rate-input-${currency}`}
                          style={{ width: 108, padding: "5px 7px", border: `1px solid ${T.border}`, borderRadius: 5, textAlign: "right" }}
                        />
                        {isEdited && isAdmin && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={(event) => savedBasis?.mode === "budget" && Number(savedBasis.fiscalYear) === Number(basisDraft.fiscalYear)
                              ? openConfirmation({ type: "budget", currency }, event.currentTarget)
                              : persistBudgetRate(currency)}
                            style={BUTTON}
                            data-testid={`fx-budget-rate-save-${currency}`}
                          >
                            Save
                          </button>
                        )}
                        {FIXED_RATES.has(currency) && <span style={{ color: T.light, fontSize: T.fsXs }}>Fixed</span>}
                      </span>
                    ) : used == null || !Number.isFinite(used) ? "—" : used.toFixed(4)}
                    {basisDraft?.mode === "as_of" && detail?.carried && (
                      <span data-testid={`fx-basis-carried-${currency}`} style={{ display: "block", color: T.amber, fontWeight: T.fwRegular, fontSize: T.fsXxs }} title={`No rate on ${basisDraft.asOfDate}; using prior rate from ${dateLabel(detail.rateDate)}.`}>
                        Carried from {dateLabel(detail.rateDate)}
                      </span>
                    )}
                  </td>
                  <td style={{ ...TD, color: T.mid }}>{dateLabel(rateDate)}</td>
                  <td
                    data-testid={`fx-basis-live-${currency}`}
                    title={liveSource || undefined}
                    style={{ ...TD, color: T.mid }}
                  >
                    {live == null ? "—" : Number(live).toFixed(4)}
                    {isBootstrapQuote(liveSource) && (
                      <span style={{ display: "block", color: T.light, fontSize: T.fsXxs }}>Bootstrap fallback</span>
                    )}
                  </td>
                  <td data-testid={`fx-basis-drift-${currency}`} style={{ ...TD, color: drift == null ? T.mid : Math.abs(drift) >= 10 ? T.red : Math.abs(drift) >= 5 ? T.amber : T.green }}>
                    {drift == null ? "—" : `${drift >= 0 ? "+" : "−"}${Math.abs(drift).toFixed(1)}%`}
                  </td>
                  <td style={{ ...TD, textAlign: "left", color: T.mid }}>{source || "—"}</td>
                  <td style={TD}>
                    {isAdmin && currency !== "USD" && !hasLiveQuote ? (
                      <button
                        type="button"
                        disabled={manualRateBusy}
                        onClick={() => {
                          setManualRateError("");
                          setManualRateDraft({
                            currency,
                            rateToUsd: detail?.source === "manual" ? String(detail.rateToUsd) : "",
                            rateDate: detail?.source === "manual"
                              ? String(detail.rateDate || "").slice(0, 10)
                              : new Date().toISOString().slice(0, 10),
                          });
                        }}
                        style={BUTTON}
                        data-testid={`fx-manual-rate-open-${currency}`}
                      >
                        {detail?.source === "manual" ? "Update manual rate" : "Set manual rate"}
                      </button>
                    ) : "—"}
                  </td>
                </tr>
              );
            })}
            {currencies.length === 0 && (
              <tr><td colSpan={7} style={{ ...TD, textAlign: "left", color: T.mid }}>No supported currency rates are available.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {manualRateDraft && (
        <section
          aria-label={`Manual ${manualRateDraft.currency} rate`}
          data-testid="fx-manual-rate-form"
          style={{ marginTop: 12, padding: 12, border: `1px solid ${T.border}`, borderRadius: 7, background: T.lightGray }}
        >
          <strong style={{ color: T.dark, fontSize: T.fsSm }}>
            Set a dated manual rate for {manualRateDraft.currency}
          </strong>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, flexWrap: "wrap", marginTop: 9 }}>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, color: T.mid, fontSize: T.fsXs }}>
              Rate to USD
              <input
                type="number"
                min="0.000001"
                step="0.000001"
                value={manualRateDraft.rateToUsd}
                onChange={(event) => setManualRateDraft((old) => ({ ...old, rateToUsd: event.target.value }))}
                disabled={manualRateBusy}
                aria-label={`${manualRateDraft.currency} manual rate to USD`}
                data-testid="fx-manual-rate-input"
                style={{ width: 150, padding: "6px 8px", border: `1px solid ${T.border}`, borderRadius: 5 }}
              />
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: 4, color: T.mid, fontSize: T.fsXs }}>
              Rate date
              <input
                type="date"
                max={new Date().toISOString().slice(0, 10)}
                value={manualRateDraft.rateDate}
                onChange={(event) => setManualRateDraft((old) => ({ ...old, rateDate: event.target.value }))}
                disabled={manualRateBusy}
                aria-label={`${manualRateDraft.currency} manual rate date`}
                data-testid="fx-manual-rate-date"
                style={{ padding: "6px 8px", border: `1px solid ${T.border}`, borderRadius: 5 }}
              />
            </label>
            <button
              type="button"
              onClick={persistManualRate}
              disabled={manualRateBusy}
              style={PRIMARY}
              data-testid="fx-manual-rate-save"
            >
              {manualRateBusy ? "Saving…" : "Save manual rate"}
            </button>
            <button
              type="button"
              onClick={() => { setManualRateDraft(null); setManualRateError(""); }}
              disabled={manualRateBusy}
              style={BUTTON}
              data-testid="fx-manual-rate-cancel"
            >
              Cancel
            </button>
          </div>
          {manualRateError && <div role="alert" data-testid="fx-manual-rate-error" style={{ marginTop: 8, color: T.red, fontSize: T.fsSm }}>{manualRateError}</div>}
          <p style={{ margin: "8px 0 0", color: T.mid, fontSize: T.fsXs }}>
            Saved as source “manual” in FX history and included in the audit log.
          </p>
        </section>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginTop: 9, color: T.mid, fontSize: T.fsXs }}>
        <span>
          {basisDraft?.mode === "live" && `Live snapshot ${currencyData?.liveSnapshot?.fetchedAt ? `last fetched ${dateLabel(currencyData.liveSnapshot.fetchedAt)}` : "not yet fetched"}.`}
          {basisDraft?.mode === "as_of" && "Missing dates use the nearest prior rate only; carried rows are flagged."}
          {basisDraft?.mode === "budget" && "Budget rates can be saved individually. Fixed USD pegs cannot be edited."}
        </span>
        {!isAdmin && <span>Read-only access</span>}
      </div>

      {confirmAction && (
        <div
          role="presentation"
          onMouseDown={(event) => { if (event.target === event.currentTarget) setConfirmAction(null); }}
          style={{ position: "fixed", inset: 0, zIndex: 1000, display: "grid", placeItems: "center", padding: 20, background: "rgba(15, 23, 42, .35)" }}
        >
          <div ref={confirmDialogRef} role="dialog" aria-modal="true" aria-labelledby="fx-basis-confirm-title" aria-describedby="fx-basis-confirm-copy" style={{ width: "min(440px, 100%)", background: T.white, borderRadius: 10, boxShadow: T.shadowEditModal, padding: 22 }}>
            <h4 id="fx-basis-confirm-title" style={{ margin: "0 0 8px", color: T.dark, fontSize: T.fsMd }}>Change conversion basis?</h4>
            <p id="fx-basis-confirm-copy" style={{ color: T.mid, fontSize: T.fsSm, lineHeight: 1.5, margin: "0 0 16px" }}>
              {confirmAction.type === "basis"
                ? `Apply ${basisModeLabel} as the saved conversion basis? USD figures across the app and all reports will change for every user. The change is recorded in the audit log.`
                : `Save this budget rate? Because FY${String(basisDraft?.fiscalYear).slice(-2)} budget is the currently saved basis, USD figures across the app and reports will change for every user. The edit is recorded in the audit log.`}
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button ref={confirmCancelRef} type="button" disabled={busy} onClick={() => setConfirmAction(null)} style={BUTTON} data-testid="fx-basis-confirm-cancel">Cancel</button>
              <button type="button" disabled={busy} onClick={confirmSave} style={PRIMARY} data-testid="fx-basis-confirm-apply">{busy ? "Saving…" : "Apply"}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
